/** Durations for the animated sequences, in milliseconds. */

/**
 * The submit sequence, in order. The card seals, the dog barges into it, it
 * shakes him back, it shrinks into a letter, and he carries it off.
 *
 * These add up to what a guest waits after pressing send, so the total is worth
 * watching: the API call runs alongside and is normally far quicker.
 */
export const LOCK_MS = 260
export const APPROACH_MS = 850
export const IMPACT_MS = 420
export const FOLD_MS = 620
export const CARRY_MS = 1500

/** How long the whole send-off takes, for anything that needs to plan around it. */
export const DELIVERY_MS = LOCK_MS + APPROACH_MS + IMPACT_MS + FOLD_MS + CARRY_MS

/** The arrival animation after a code is accepted. */
export const UNLOCK_MS = 1500

/** How long the dog takes to wander across the page on one of his cameos. */
export const WANDER_MS = 9000

/**
 * The terrier's whole visit: on, sat down for a while, and back off again.
 *
 * The fractions at which he arrives and leaves live in WanderingTerrier, and have
 * to agree with the terrier-visit keyframes.
 */
export const VISIT_MS = 11000

/**
 * Whether the guest has asked their system to keep motion to a minimum. Read at
 * call time rather than cached, because it can change while the page is open.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * The time to actually wait for an animation of nominal length `ms`. Reduced
 * motion collapses it to nothing, which pairs with the CSS in animations.css
 * that skips the corresponding keyframes.
 */
export function duration(ms: number): number {
  return prefersReducedMotion() ? 0 : ms
}

/** Resolves after `ms`, or on the next tick when `ms` is 0. */
export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    if (ms <= 0) {
      resolve()
      return
    }
    window.setTimeout(resolve, ms)
  })
}
